import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { feedAPI } from '../../lib/api';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Loader2, Sparkles, Wand2, Copy } from 'lucide-react';
import { Input } from '../ui/input';

const modules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        ['link', 'image'],
        ['clean'],
    ],
};

const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
];

const CreatePostModal = ({ isOpen, onClose, onSuccess, post }) => {
    const [content, setContent] = useState('');
    const [isPublished, setIsPublished] = useState(true);
    const [loading, setLoading] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        if (post) {
            setContent(post.content);
            setIsPublished(post.is_published);
        } else {
            setContent('');
            setIsPublished(true);
        }
        setAiPrompt('');
    }, [post, isOpen]);

    const handleGenerateAI = async () => {
        if (!aiPrompt.trim()) {
            toast.error('Please enter a prompt for the AI');
            return;
        }

        try {
            setGenerating(true);
            const response = await feedAPI.generateAiPost(aiPrompt);
            let aiContent = response.data.content;

            // Fallback: If AI still sends markdown-style bolding (**text**), convert to <strong>
            aiContent = aiContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

            // If the content doesn't smell like HTML (no <p> or <div>), wrap newlines in <p>
            if (!/<[a-z][\s\S]*>/i.test(aiContent)) {
                aiContent = aiContent
                    .split('\n')
                    .filter(line => line.trim())
                    .map(line => `<p>${line}</p>`)
                    .join('');
            }

            setContent(aiContent);
            toast.success('Generated post with AI');
        } catch (error) {
            console.error('AI generation failed:', error);
            toast.error('Failed to generate post with AI');
        } finally {
            setGenerating(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!content.trim() || content === '<p><br></p>') {
            toast.error('Content is required');
            return;
        }

        try {
            setLoading(true);
            const data = { content, is_published: isPublished };

            if (post) {
                await feedAPI.updatePost(post.id, data);
                toast.success('Post updated successfully');
            } else {
                await feedAPI.createPost(data);
                toast.success('Post created successfully');
            }

            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to save post:', error);
            toast.error(error.response?.data?.detail || 'Failed to save post');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[850px] h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
                <DialogHeader className="p-6 border-b shrink-0">
                    <div className="flex items-center justify-between">
                        <DialogTitle>{post ? 'Edit Post' : 'Create New Post'}</DialogTitle>
                        {/* <div className="flex items-center gap-2 text-xs font-bold text-[#fdba12] bg-[#fdba12]/10 border border-[#fdba12]/20 px-3 py-1 rounded-full shadow-sm">
                            <Sparkles className="w-3 h-3" />
                            AI Powered
                        </div> */}
                    </div>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">

                        {/* AI Assistant Section */}
                        <div className="bg-gradient-to-br from-[#fdba12]/10 to-white p-5 rounded-xl border border-[#fdba12]/20 shadow-sm space-y-4">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-[#fdba12] rounded-lg">
                                    <Wand2 className="w-4 h-4 text-black" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-800">AI Post Assistant</h3>
                                    <p className="text-xs text-slate-500">Describe what you want to post and AI will write it for you.</p>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <Input
                                    placeholder="e.g. A welcome post for new joinee Akshay, or a company holiday announcement for Holi..."
                                    value={aiPrompt}
                                    onChange={(e) => setAiPrompt(e.target.value)}
                                    className="bg-white/80 border-[#fdba12]/30 focus:border-[#fdba12] focus:ring-[#fdba12]/20 transition-all text-sm h-10"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleGenerateAI();
                                        }
                                    }}
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="bg-[#fdba12] text-black font-bold hover:bg-[#fdba12]/90 hover:text-black border-none shrink-0 shadow-sm"
                                    onClick={handleGenerateAI}
                                    disabled={generating || !aiPrompt.trim()}
                                >
                                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                                    {generating ? 'Drafting...' : 'Generate'}
                                </Button>
                            </div>
                        </div>

                        <div className="space-y-2 flex-1 flex flex-col min-h-[300px]">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-semibold text-slate-700 uppercase tracking-tight">Post Content</Label>
                                <span className="text-xs text-slate-400">Manual editing enabled</span>
                            </div>
                            <div className="flex-1 border border-slate-200 rounded-xl overflow-hidden flex flex-col bg-white shadow-sm">
                                <ReactQuill
                                    theme="snow"
                                    value={content}
                                    onChange={setContent}
                                    modules={modules}
                                    formats={formats}
                                    className="flex-1 flex flex-col quill-editor"
                                    placeholder="Write your announcement here or use the AI assistant above..."
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-100">
                            <div className="flex items-center space-x-3">
                                <Switch
                                    id="is-published"
                                    checked={isPublished}
                                    onCheckedChange={setIsPublished}
                                />
                                <div>
                                    <Label htmlFor="is-published" className="cursor-pointer text-sm font-semibold text-slate-800">
                                        Publish immediately
                                    </Label>
                                    <p className="text-[11px] text-slate-500">Visible to all employees across the organization.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="p-6 border-t bg-white shrink-0">
                        <Button type="button" variant="ghost" onClick={onClose} disabled={loading || generating}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading || generating} className="min-w-[140px] bg-slate-900 hover:bg-slate-800 text-white rounded-lg shadow-md transition-all">
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                post ? 'Save Changes' : 'Create & Post'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default CreatePostModal;
